import { Box, Typography, Button, styled, Paper } from "@mui/material";
import { LocalDining, TrendingUp, Analytics, Speed } from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const gradientFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedWrapper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 40px rgba(0,0,0,0.15)'
  }
}));

export const HeaderDailyServings = () => {
  const theme = useTheme();

  return (
    <AnimatedWrapper sx={{ mb: 4 }}>
      <Box sx={{ 
        p: { xs: 3, md: 4 },
        background: `linear-gradient(135deg, 
          ${theme.palette.primary.dark} 0%, 
          ${theme.palette.secondary.main} 100%)`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 2
      }}>
        {/* Left Section - Icon & Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalDining sx={{ 
            fontSize: { xs: 40, md: 48 },
            color: 'common.white',
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
            animation: `${float} 3s ease-in-out infinite`,
          }} />
          
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              color: 'common.white',
              lineHeight: 1.2,
              mb: 0.5
            }}>
              Suivi analytique
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 400
            }}>
              Optimisation des coûts et intelligence opérationnelle
            </Typography>
          </Box>
        </Box>

        {/* Right Section - Stats & Button */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 3,
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          {/* Quick Stats */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            '& .statItem': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(4px)'
            }
          }}>
            <Box className="statItem">
              <Analytics sx={{ fontSize: 18, color: 'white' }} />
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                Coûts
              </Typography>
            </Box>
            <Box className="statItem">
              <Speed sx={{ fontSize: 18, color: 'white' }} />
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                Performance
              </Typography>
            </Box>
          </Box>

          <Button 
            component={Link}
            to="/DailyServingsAnalytics"
            variant="contained" 
            size="small"
            endIcon={<TrendingUp />}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: 2,
              bgcolor: 'white',
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
                transform: 'scale(1.02)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
              }
            }}
          >
            Dashboard
          </Button>
        </Box>

        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '100%',
          background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 100%)',
          pointerEvents: 'none'
        }} />
      </Box>
    </AnimatedWrapper>
  );
};